interface DPEData {
  living_area: number;
  climate_zone?: string;
  energy_class?: string;
  primary_energy_consumption?: number;
  construction_year?: number;

  deperditions_baies_vitrees?: number;
  deperditions_murs?: number;
  deperditions_planchers_hauts?: number;
  deperditions_planchers_bas?: number;
  deperditions_ponts_thermiques?: number;
  deperditions_enveloppe?: number;
  deperditions_renouvellement_air?: number;

  besoin_chauffage?: number;
  besoin_ecs?: number;
  conso_chauffage_ef?: number;
  conso_ecs_ef?: number;
}

interface EstimatedHeatLoss {
  deperditions_baies_vitrees: number;
  deperditions_murs: number;
  deperditions_planchers_hauts: number;
  deperditions_planchers_bas: number;
  deperditions_ponts_thermiques: number;
  deperditions_enveloppe: number;
  deperditions_renouvellement_air: number;
  besoin_chauffage: number;
  besoin_ecs: number;
  conso_chauffage_ef: number;
  conso_ecs_ef: number;
  is_estimated: boolean;
}

const ENERGY_CLASS_COEFFICIENTS: Record<string, number> = {
  A: 0.3,
  B: 0.5,
  C: 0.7,
  D: 1.0,
  E: 1.4,
  F: 1.8,
  G: 2.3,
};

const CONSTRUCTION_YEAR_INSULATION_FACTOR: Array<{ maxYear: number; factor: number }> = [
  { maxYear: 1948, factor: 2.5 },
  { maxYear: 1974, factor: 2.0 },
  { maxYear: 1982, factor: 1.5 },
  { maxYear: 2000, factor: 1.0 },
  { maxYear: 2012, factor: 0.7 },
  { maxYear: 3000, factor: 0.5 },
];

const CLIMATE_ZONE_HEATING_FACTOR: Record<string, number> = {
  H1: 1.2,
  H2: 1.0,
  H3: 0.8,
};

const AIR_RENEWAL_RATE_BY_YEAR: Array<{ maxYear: number; rate: number }> = [
  { maxYear: 1948, rate: 1.2 },
  { maxYear: 1974, rate: 1.0 },
  { maxYear: 1982, rate: 0.8 },
  { maxYear: 2000, rate: 0.6 },
  { maxYear: 2012, rate: 0.5 },
  { maxYear: 3000, rate: 0.4 },
];

function getInsulationFactorByYear(year: number | undefined): number {
  if (!year) return 1.5;

  const range = CONSTRUCTION_YEAR_INSULATION_FACTOR.find(r => year <= r.maxYear);
  return range?.factor || 1.0;
}

function getEnergyClassCoefficient(energyClass: string | undefined): number {
  return ENERGY_CLASS_COEFFICIENTS[energyClass || 'D'] || 1.0;
}

function getClimateZoneFactor(climateZone: string | undefined): number {
  return CLIMATE_ZONE_HEATING_FACTOR[climateZone || 'H2'] || 1.0;
}

function getAirRenewalRate(year: number | undefined): number {
  if (!year) return 0.8;

  const range = AIR_RENEWAL_RATE_BY_YEAR.find(r => year <= r.maxYear);
  return range?.rate || 0.6;
}

export function estimateHeatLossFromExistingData(dpe: DPEData): EstimatedHeatLoss {
  const livingArea = dpe.living_area || 70;
  const energyCoeff = getEnergyClassCoefficient(dpe.energy_class);
  const insulationFactor = getInsulationFactorByYear(dpe.construction_year);
  const climateFactor = getClimateZoneFactor(dpe.climate_zone);

  const baseHeatLoss = livingArea * 5 * energyCoeff * insulationFactor * climateFactor;

  const deperditions_baies_vitrees = Math.round(baseHeatLoss * 0.25);
  const deperditions_murs = Math.round(baseHeatLoss * 0.35);
  const deperditions_planchers_hauts = Math.round(baseHeatLoss * 0.25);
  const deperditions_planchers_bas = Math.round(baseHeatLoss * 0.05);
  const deperditions_ponts_thermiques = Math.round(baseHeatLoss * 0.05);
  const deperditions_enveloppe = Math.round(baseHeatLoss);

  const volume = livingArea * 2.5;
  const airRenewalRate = getAirRenewalRate(dpe.construction_year);
  const airHeatCapacity = 0.34;
  const deperditions_renouvellement_air = Math.round(
    volume * airRenewalRate * airHeatCapacity * energyCoeff * climateFactor
  );

  const primaryConsumption = dpe.primary_energy_consumption || 250;
  const besoin_chauffage = Math.round(livingArea * (primaryConsumption * 0.60));
  const besoin_ecs = Math.round(livingArea * 20);

  const conso_chauffage_ef = Math.round(besoin_chauffage * 1.1);
  const conso_ecs_ef = Math.round(besoin_ecs * 1.1);

  return {
    deperditions_baies_vitrees,
    deperditions_murs,
    deperditions_planchers_hauts,
    deperditions_planchers_bas,
    deperditions_ponts_thermiques,
    deperditions_enveloppe,
    deperditions_renouvellement_air,
    besoin_chauffage,
    besoin_ecs,
    conso_chauffage_ef,
    conso_ecs_ef,
    is_estimated: true,
  };
}

export function enrichDPEWithEstimates(dpe: DPEData): DPEData & { has_estimated_data?: boolean } {
  const hasAllRequiredData =
    dpe.deperditions_baies_vitrees &&
    dpe.deperditions_murs &&
    dpe.deperditions_planchers_hauts &&
    dpe.deperditions_renouvellement_air &&
    dpe.besoin_chauffage &&
    dpe.besoin_ecs;

  if (hasAllRequiredData) {
    return { ...dpe, has_estimated_data: false };
  }

  const estimates = estimateHeatLossFromExistingData(dpe);

  return {
    ...dpe,
    deperditions_baies_vitrees: dpe.deperditions_baies_vitrees || estimates.deperditions_baies_vitrees,
    deperditions_murs: dpe.deperditions_murs || estimates.deperditions_murs,
    deperditions_planchers_hauts: dpe.deperditions_planchers_hauts || estimates.deperditions_planchers_hauts,
    deperditions_planchers_bas: dpe.deperditions_planchers_bas || estimates.deperditions_planchers_bas,
    deperditions_ponts_thermiques: dpe.deperditions_ponts_thermiques || estimates.deperditions_ponts_thermiques,
    deperditions_enveloppe: dpe.deperditions_enveloppe || estimates.deperditions_enveloppe,
    deperditions_renouvellement_air: dpe.deperditions_renouvellement_air || estimates.deperditions_renouvellement_air,
    besoin_chauffage: dpe.besoin_chauffage || estimates.besoin_chauffage,
    besoin_ecs: dpe.besoin_ecs || estimates.besoin_ecs,
    conso_chauffage_ef: dpe.conso_chauffage_ef || estimates.conso_chauffage_ef,
    conso_ecs_ef: dpe.conso_ecs_ef || estimates.conso_ecs_ef,
    has_estimated_data: true,
  };
}
